import { useScrollbarHarmonisation } from '@atlaskit/app-provider/use-scrollbar-harmonisation';
import { getContentIdFromPath } from '@databases/database-state';
import { DatabaseContentContextProvider } from '@databases/database-state';
import { currentBridgeData } from '@databases/database-state/state/bridge-data';
import AppContent from '@databases/frontend/AppContent';
import AppWithContext from '@databases/frontend/AppWithContext';
import {
  type TableV2HostDependencies,
  TableV2HostProvider,
} from '@databases/frontend/table-v2-host';
import { perf } from '@databases/logs/performance';
import { appStore } from '@databases/redux/store';
import { setContext } from '@databases/utils/redux/context/actions';
import { withoutThrottledBatching } from '@databases/utils/redux/redux';
import React, { useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

function getParentContentId() {
  try {
    // oxlint-disable core/no-restricted-syntax
    // eslint-disable-next-line no-restricted-syntax
    const pathname = window.top && window.top.location.pathname;
    // oxlint-enable core/no-restricted-syntax

    return pathname ? getContentIdFromPath(pathname) : null;
  } catch {
    // can't access window.top if cross-origin
    return null;
  }
}
type AppProps = {
  tableV2Host?: TableV2HostDependencies;
  entryId: string | null;
  fieldId: string | null;
  referencing: boolean;
  savedViewId: string | null;
  unsavedViewId: string | null;
};

const App: React.FC<AppProps> = ({
  tableV2Host,
  entryId,
  fieldId,
  referencing,
  savedViewId,
  unsavedViewId,
}) => {
  const {
    activationId,
    cloudId,
    contentId,
    contentReferenceId: referenceId,
    identity,
    spaceId,
    spaceKey,
    spaceName,
    title,
  } = useSnapshot(currentBridgeData).data;

  perf.timeLog('app-initialization', 'standalone-app-render');

  useScrollbarHarmonisation(isFeatureFlagEnabled('scrollbarHarmonisation'));

  useEffect(() => {
    // Focus iframe to enable keyboard shortcuts
    window.focus();
  }, []);

  useEffect(() => {
    // eslint-disable-next-line require-await
    withoutThrottledBatching(async () => {
      appStore.dispatch(
        setContext({ space: { key: spaceKey }, userId: identity.accountId }),
      );
    });
  }, [spaceKey, identity]);

  const contextValue = useMemo(
    () => ({
      activationId,
      cloudId,
      contentId,
      entryId,
      fieldId,
      parentContentId: getParentContentId(),
      referenceId,
      referencing,
      savedViewId,
      setContentId: () => {},
      spaceId,
      spaceKey,
      spaceName,
      title,
      unsavedViewId,
    }),
    [
      activationId,
      cloudId,
      contentId,
      entryId,
      fieldId,
      referenceId,
      referencing,
      savedViewId,
      spaceId,
      spaceKey,
      spaceName,
      title,
      unsavedViewId,
    ],
  );

  return (
    <DatabaseContentContextProvider value={contextValue}>
      <AppWithContext aaid={identity.accountId}>
        <TableV2HostProvider value={tableV2Host}>
          <AppContent />
        </TableV2HostProvider>
      </AppWithContext>
    </DatabaseContentContextProvider>
  );
};

export default React.memo(App);
